import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { inr } from "@/lib/format";

const empty = () => ({ label: "", quantity: 1, unit_price: 0, amount: 0 });

export default function BillDialog({ open, onOpenChange, patient, consultationId }) {
  const [items, setItems] = useState([{ label: "Consultation", quantity: 1, unit_price: 500, amount: 500 }]);
  const [taxPct, setTaxPct] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  const subtotal = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const tax = +(subtotal * (taxPct || 0) / 100).toFixed(2);
  const total = +(subtotal + tax - (discount || 0)).toFixed(2);

  const submit = async () => {
    if (!patient) return;
    if (!items.length) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      const { data } = await api.post("/bills", {
        patient_id: patient.id,
        consultation_id: consultationId || null,
        items: items.map((i) => ({ label: i.label, quantity: Number(i.quantity), unit_price: Number(i.unit_price), amount: Number(i.amount) })),
        tax_percent: Number(taxPct || 0), discount: Number(discount || 0), notes,
      });
      setSaved(data);
      toast.success("Bill generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create bill");
    } finally { setBusy(false); }
  };

  const shareUrl = saved ? `${window.location.origin}/share/bill/${saved.share_token}` : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[24px] max-w-2xl" data-testid="bill-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Generate bill</DialogTitle>
        </DialogHeader>

        {saved ? (
          <div className="space-y-4 mt-2">
            <div className="text-sm">Bill generated for <b>{patient?.name}</b>. Total: <b>{inr(saved.total)}</b>.</div>
            <div className="card-soft p-4 text-xs font-mono break-all border border-[#EFECE5]">{shareUrl}</div>
            <div className="flex gap-2 justify-end">
              <a href={shareUrl} target="_blank" rel="noreferrer" className="btn-outline-emerald"><ExternalLink size={14} /> Open</a>
              <button className="btn-emerald" onClick={async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }}>Copy link</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`bill-item-${i}`}>
                  <input className="input-clean col-span-5" placeholder="Item" value={it.label}
                    onChange={(e) => setItems(items.map((x, ix) => ix === i ? { ...x, label: e.target.value } : x))} />
                  <input type="number" min="0" className="input-clean col-span-2" placeholder="Qty" value={it.quantity}
                    onChange={(e) => setItems(items.map((x, ix) => ix === i ? { ...x, quantity: e.target.value, amount: (Number(e.target.value) || 0) * (Number(x.unit_price) || 0) } : x))} />
                  <input type="number" min="0" className="input-clean col-span-2" placeholder="Price" value={it.unit_price}
                    onChange={(e) => setItems(items.map((x, ix) => ix === i ? { ...x, unit_price: e.target.value, amount: (Number(x.quantity) || 0) * (Number(e.target.value) || 0) } : x))} />
                  <div className="col-span-2 text-right text-sm font-semibold">{inr(it.amount)}</div>
                  <button className="col-span-1 text-red-500" onClick={() => setItems(items.filter((_, ix) => ix !== i))}><Trash2 size={16} /></button>
                </div>
              ))}
              <button className="btn-outline-emerald" onClick={() => setItems([...items, empty()])} data-testid="add-bill-item"><Plus size={14} /> Add item</button>
            </div>
            <div className="grid grid-cols-3 gap-3 pt-2">
              <div>
                <label className="field-label block mb-1">Tax %</label>
                <input type="number" min="0" className="input-clean" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} />
              </div>
              <div>
                <label className="field-label block mb-1">Discount</label>
                <input type="number" min="0" className="input-clean" value={discount} onChange={(e) => setDiscount(e.target.value)} />
              </div>
              <div className="text-right self-end">
                <div className="text-xs text-[#8A9992]">Subtotal {inr(subtotal)} · Tax {inr(tax)}</div>
                <div className="text-2xl font-semibold text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>{inr(total)}</div>
              </div>
            </div>
            <div>
              <label className="field-label block mb-1">Notes</label>
              <input className="input-clean" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment received in cash / UPI…" />
            </div>
            <div className="flex justify-end pt-2">
              <button className="btn-emerald" disabled={busy} onClick={submit} data-testid="generate-bill-btn">{busy ? "Generating…" : "Generate bill"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
